import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import classnames from 'classnames';
import { translate } from '@estee/elc-service';
import { Button } from '@estee/elc-buttons';

import { classes } from '~utils/Classes';
import { VtoCTAButtonViewController } from '~controllers/VtoCTAButtonViewController';
import {
    VTO_MAKEUP_BUTTON,
    VTO_FOUNDATION_BUTTON
} from '~constants/DataTestIdAttributes';
import { IVtoCTAButtonServiceConfig } from '~interfaces/IVto';
import { Desktop, Mobile } from '@estee/elc-layouts';
import { Breakpoints } from '@estee/elc-base-theme';

export interface IVtoCTAButton {
    config: IVtoCTAButtonServiceConfig;
    translations: IVtoTranslations;
    viewController: VtoCTAButtonViewController;
}

export interface IVtoTranslations {
    vtoFoundationButtonLabel: string;
    vtoMakeupButtonLabel: string;
}

const VtoCtaWrapper = styled.div`
    text-align: center;
    cursor: pointer;
`;

@translate(['vtoFoundationButtonLabel', 'vtoMakeupButtonLabel'])
@observer
class VtoCTAButton extends React.Component<IVtoCTAButton> {
    constructor(props: IVtoCTAButton) {
        super(props);
        props.viewController.initialize(props.config);
    }

    public componentDidMount() {
        this.props.viewController.openVtoOnLoad();
    }

    public render() {
        const { viewController, config, translations } = this.props;
        const {
            data: { isVtoOpened },
            initVto,
            openVtoFoundationQrCodeOverlay
        } = viewController;
        const {
            isVtoFoundationEnabled,
            isVtoMakeupEnabled,
            isColorExperience,
            isFoundationExperience
        } = config;

        const { vtoFoundationButtonLabel, vtoMakeupButtonLabel } = translations;
        const isVtoCtaDisabled = isVtoOpened || (!isColorExperience && !isFoundationExperience);
        const vtoButtonTestId = isVtoFoundationEnabled ? VTO_FOUNDATION_BUTTON : VTO_MAKEUP_BUTTON;

        const vtoContainerClassNames = classnames({
            [classes('vto-foundation-button')]: isVtoFoundationEnabled,
            [classes('vto-makeup-button')]: isVtoMakeupEnabled
        });

        const buttonLabel = isVtoFoundationEnabled
            ? vtoFoundationButtonLabel
            : vtoMakeupButtonLabel;

        const desktopClickHandler = isVtoFoundationEnabled
            ? openVtoFoundationQrCodeOverlay
            : initVto;

        const buttonProps = {
            title: buttonLabel,
            className: classes('vto-button'),
            disabled: isVtoCtaDisabled,
            ['data-test-id']: vtoButtonTestId
        };

        return (
            <VtoCtaWrapper className={vtoContainerClassNames}>
                <Desktop minWidth={Breakpoints.desktop}>
                    {() => (
                        <Button {...buttonProps} onClick={desktopClickHandler}>
                            {buttonLabel}
                        </Button>
                    )}
                </Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() => (
                        <Button {...buttonProps} onClick={initVto}>
                            {buttonLabel}
                        </Button>
                    )}
                </Mobile>
            </VtoCtaWrapper>
        );
    }
}

export default VtoCTAButton;
